/* Cadence, local time, timezone and weekdays — the four controls both schedule forms need.
 *
 * **Shared rather than written twice, because the recurrence is the same fact on both surfaces.**
 * The backend went out of its way to have one `Recurrence` in `kernel.scheduler` for exactly this
 * reason: two copies of "what does weekly mean" would each pass their own tests while disagreeing
 * about a weekday set. Two copies of the *form* is the same defect one layer out — and the one that
 * matters more, because it is where a reader chooses the value.
 *
 * The harvest schedule adds nothing to these; the execution schedule adds strategy and severity
 * floor, which are its own and live in its own form.
 */

import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { SelectField, TextField } from '@/shared/ui/Field';

import {
  TIMEZONES,
  WEEKDAY_ORDER,
  weekdayKey,
  withCadence,
  type RecurrenceDraft,
} from '../model/presentation';

export interface RecurrenceFieldsProps {
  readonly value: RecurrenceDraft;
  readonly onChange: (next: RecurrenceDraft) => void;
}

export function RecurrenceFields({
  value,
  onChange,
}: RecurrenceFieldsProps): ReactElement {
  const { t } = useTranslation();

  const toggleDay = (day: number): void => {
    const has = value.weekdays.includes(day);
    onChange({
      ...value,
      weekdays: has
        ? value.weekdays.filter((each) => each !== day)
        : [...value.weekdays, day].sort((a, b) => a - b),
    });
  };

  return (
    <>
      <div className="lq-sched__form">
        <SelectField
          label={t('scheduling.form.cadence')}
          value={value.cadence}
          options={[
            { value: 'daily', label: t('scheduling.cadence.daily') },
            { value: 'weekly', label: t('scheduling.cadence.weekly') },
          ]}
          onChange={(next) => {
            onChange(withCadence(value, next));
          }}
        />
        <TextField
          label={t('scheduling.form.localTime')}
          value={value.localTime}
          /* **24-hour, said on the control rather than only in the refusal.** The field accepts
             `HH:MM` and nothing else, and the number a reader is most likely to want is an
             out-of-hours one — so `1:30` typed for half past one in the morning and `1:30` typed
             for the afternoon are the same three characters, and only the stated convention
             separates them. The card renders the result back with an explicit AM/PM, which is
             where the reader confirms they got the one they meant. */
          hint={t('scheduling.form.localTimeHint')}
          placeholder="02:00"
          onChange={(next) => {
            onChange({ ...value, localTime: next });
          }}
        />
        <SelectField
          label={t('scheduling.form.timezone')}
          value={value.timezone}
          options={TIMEZONES.map((zone) => ({ value: zone, label: zone }))}
          onChange={(next) => {
            onChange({ ...value, timezone: next });
          }}
        />
      </div>

      {/* **The IANA rule, stated on the control rather than only in a refusal.** An offset such as
          `UTC+05:30` is refused by the domain and by a CHECK constraint, and the reason is worth a
          sentence: it cannot express a daylight-saving transition, so a schedule stored that way
          moves by an hour twice a year and the first symptom is a full scan during business hours.
          The picker cannot produce one, which is the real enforcement — this says why. */}
      <p className="lq-sched__note">{t('scheduling.form.timezoneHint')}</p>

      {value.cadence === 'weekly' ? (
        <fieldset className="lq-sched__days">
          <legend className="lq-sched__label">{t('scheduling.form.weekdays')}</legend>
          {WEEKDAY_ORDER.map((day) => (
            <label key={day} className="lq-sched__day">
              <input
                type="checkbox"
                checked={value.weekdays.includes(day)}
                onChange={() => {
                  toggleDay(day);
                }}
              />
              {t(weekdayKey(day))}
            </label>
          ))}
        </fieldset>
      ) : null}
    </>
  );
}
